import { twMerge } from "tailwind-merge";
import useTabIndicatorPosition from "./useTabIndicator";

export interface Tab<T extends string> {
  id: T;
  label: (isSelected: boolean) => React.ReactNode;
}

interface TabsProps<T extends string> {
  tabs: Tab<T>[];
  selected: T;
  onChange: (id: T) => void;
  className?: string;
  variant?: "pill" | "underline";
}

export function Tabs<T extends string>({
  tabs,
  selected,
  onChange,
  className,
  variant = "pill",
}: TabsProps<T>) {
  const { containerRef, buttonRefs, indicator } = useTabIndicatorPosition({
    selected,
  });

  return (
    <div
      ref={containerRef}
      className={twMerge(
        "relative flex",
        variant === "pill" && "gap-1 rounded-full bg-[#F1F1F3] p-1",
        className,
      )}
    >
      <div
        className={twMerge(
          "absolute transition-all duration-200 ease-in-out",
          variant === "pill"
            ? "inset-y-1 rounded-full bg-white shadow-[0px_1px_1px_0px_rgba(0,0,0,0.04),0px_1px_3px_0px_rgba(0,0,0,0.1)]"
            : "bg-brand-blue bottom-0 h-0.5",
        )}
        style={{ left: indicator.left, width: indicator.width }}
      />
      {tabs.map((tab) => (
        <button
          key={tab.id}
          ref={(el) => {
            if (el) buttonRefs.current.set(tab.id, el);
            else buttonRefs.current.delete(tab.id);
          }}
          onClick={() => onChange(tab.id)}
          className={twMerge(
            "relative z-10 cursor-pointer border-0 bg-transparent outline-none",
            variant === "pill"
              ? "rounded-full px-5 py-2 text-[13.5px] font-semibold whitespace-nowrap transition-colors duration-150"
              : "px-4 py-3 text-[13px] font-medium transition-colors duration-150",
            tab.id === selected
              ? variant === "pill"
                ? "text-[#1d1d1f]"
                : "text-brand-blue font-bold"
              : variant === "pill"
                ? "hover:text-content-emphasis text-[#4b5563]"
                : "text-content-muted hover:text-content-emphasis font-medium",
          )}
        >
          {tab.label(tab.id === selected)}
        </button>
      ))}
    </div>
  );
}
